import { getLocale } from '~/shell/middleware/i18next';
import { AreaRedirectBehaviour, optionalProtectedLoader } from '~/shell/utils/authUtil.server';
import { redirect, useLocation } from 'react-router';
import { PgArea, TgArea } from '~/common/types/area';
import { AreaSelectorItem } from '~/shell/routes/AreaSelectionRoute';
import { useTranslation } from 'react-i18next';
import TransgourmetProdegaLogo from '~/assets/logos/transgourmet-prodega-logo';
import { LanguageSwitcher } from '~/shell/components/LanguageSwitcher';
import type { Route } from '../../../.react-router/types/app/shell/routes/+types/ShortcutResolverRoute';
import { processLinkRoute } from '~/shell/utils/shortLinkUtil';
import { localeCookie } from '~/shell/cookies';

export const TARGET_URL = 'targetUrl';
export const REFERRER_URL_HINT = 'referrerUrlHint';

interface ParsedUrl {
  pathname: string;
  searchParams: URLSearchParams;
}

function parsePathAndQuery(url: string): ParsedUrl {
  const queryIndex = url.indexOf('?');

  if (queryIndex === -1) {
    return {
      pathname: url,
      searchParams: new URLSearchParams()
    };
  }

  const pathname = url.substring(0, queryIndex);
  const queryString = url.substring(queryIndex + 1);
  const searchParams = new URLSearchParams(queryString);

  return {
    pathname,
    searchParams
  };
}

function replaceLangInRelativeUrl(relativeUrl: string, lang: string) {
  const parts = relativeUrl.split('/').filter(Boolean);
  parts[0] = lang;
  return `/${parts.join('/')}`;
}

function buildTargetUrl(baseUrl: string, targetUrl: string): string {
  const newUrlSearchQuery = new URLSearchParams();
  newUrlSearchQuery.set(TARGET_URL, targetUrl);
  return `${baseUrl}?${newUrlSearchQuery.toString()}`;
}

export const loader = optionalProtectedLoader(
  async ({ context, url }) => {
    const lang = getLocale(context);

    const targetUrl = url.searchParams.get(TARGET_URL) ?? '';
    const referrerUrlHint = url.searchParams.get(REFERRER_URL_HINT);
    const parseUrl = parsePathAndQuery(targetUrl);

    const result = processLinkRoute(parseUrl.pathname, parseUrl.searchParams, lang, referrerUrlHint);

    if (result.redirectUrl) {
      throw redirect(result.redirectUrl);
    }

    if (result.targetLang !== lang) {
      const newRedirectUrl = replaceLangInRelativeUrl(targetUrl, result.targetLang);
      throw redirect(buildTargetUrl(url.pathname, newRedirectUrl), {
        headers: { 'Set-Cookie': await localeCookie.serialize(result.targetLang as any) }
      });
    }

    return {
      tgUrl: result.tgUrl,
      pgUrl: result.pgUrl,
      targetLang: lang
    };
  },
  { skipAreaCheck: true, skipLangCheck: true, areaRedirectBehavior: AreaRedirectBehaviour.None }
);

export default function ShortcutResolverRoute({ loaderData }: Route.ComponentProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const currentUrl = location.pathname + location.search + location.hash;

  return (
    <div>
      <div className={'desktop:h-[150px]'}>
        <div className={'flex justify-end pt-2 desktop:pt-4 pr-3 desktop:pr-4'}>
          <LanguageSwitcher
            alternativeLanguage={loaderData.targetLang}
            redirectUrl={currentUrl}
            getRedirectUrl={lang => {
              const urlSearchParams = new URLSearchParams(location.search);
              const targetUrl = urlSearchParams.get(TARGET_URL) ?? '';
              const newRedirectUrl = replaceLangInRelativeUrl(targetUrl, lang);
              return buildTargetUrl(location.pathname, newRedirectUrl);
            }}
          />
        </div>
      </div>
      <div className="grid place-items-center w-full min-h-[calc(100vh-300px)]">
        <div>
          <div className="flex justify-center p-12 ">
            <div className="w-[260px] desktop:w-[360px] flex flex-col gap-4 justify-center items-center mb-8 desktop:mb-0">
              <TransgourmetProdegaLogo />
            </div>
          </div>
          <div className={'grid grid-cols-1 md:grid-cols-2 gap-4 '}>
            {loaderData.pgUrl && (
              <AreaSelectorItem
                area={PgArea}
                title={t('Ich möchte zu Prodega Easy (Abholung).')}
                link={loaderData.pgUrl}
                actionText={t('Zu Prodega-Easy')}
              />
            )}
            {loaderData.tgUrl && (
              <AreaSelectorItem
                area={TgArea}
                title={t('Ich möchte zum Transgourmet-Webshop (Belieferung).')}
                link={loaderData.tgUrl}
                actionText={t('Zum Transgourmet-Webshop')}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
